import { errorSafe } from 'app/infrastructure/redux';
import * as actions from './action-types';
import { reportLayoutTypeContext } from '../../constants';
import { ApplicationState } from 'app/types-store';
import { ActionDispatching, FavouriteDefinition, Value } from 'app/types-shared';
import {
  RequestExecuteFavourite,
  ReceiveExecuteFavourite,
  FailExecuteFavourite
} from './action-types';
import * as routerActions from 'app/area/shared/navigation';

import { ReportCommonApi } from 'app/area/reports/types-api';
import { DrillDownModel } from 'app/components/favourite-layout/favourite/common/drill-down-model';
import { selectChartState } from 'app/area/reports/selectors';
import { selectLayoutTypeForSelectedReport } from 'app/area/favourite/selectors';
import { actionCreators as favouriteActionCreators } from 'app/area/favourite/actions';
import { codeItemToStoreValue, storeValueToCodeItem } from 'app/infrastructure/data/codes';
import { ComplexValue } from 'ui';

// eslint:disable-next-line:max-func-body-length
export const actionCreators = (api: ReportCommonApi, instanceKey: string) => {
  const {
    setWorkingDefinitionLayoutType,
    setWorkingDefinitionDrillDownReport,
    setWorkingDefinitionDrillDownLayoutType
  } = favouriteActionCreators(instanceKey);

  return {
    clearState: (): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        dispatch(new actions.ClearSelectionReportState(instanceKey));
      }),
    loadLayoutTypes: (reportId: number): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        if (reportId) {
          dispatch(new actions.RequestLayoutTypes(instanceKey));
          const result = await api.loadLayoutTypes(reportId);
          dispatch(new actions.ResponseLayoutTypes(instanceKey, result.data));
        }
      }),
    setLayoutType: (type: Value): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        dispatch(setWorkingDefinitionLayoutType(type));
      }),
    favouriteNotValid: (): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: () => ApplicationState) => {
        const areaState = selectChartState(instanceKey)(getState());
        if (areaState.favouriteValid) {
          dispatch(new actions.FavouriteNotValid(instanceKey));
        }
      }),
    executeFavourite: (
      definition: FavouriteDefinition,
      layoutTypeContextId: number
    ): ActionDispatching<any, ApplicationState> =>
      errorSafe(async (dispatch, getState: () => ApplicationState) => {
        dispatch(new RequestExecuteFavourite(instanceKey));

        const areaState = selectChartState(instanceKey)(getState());
        const requestId = areaState.executeRequestId;
        const layoutType = selectLayoutTypeForSelectedReport(instanceKey)(getState());

        const {
          favourite: { v: favouriteId },
          selectedReport: { v: reportId }
        } = definition;

        try {
          const [layoutModel, favourite] = await Promise.all([
            api.reportLayout(
              favouriteId,
              reportId,
              layoutType?.v > 0 ? layoutType?.v : 1,
              layoutTypeContextId ?? reportLayoutTypeContext
            ),
            api.executeFavourite(definition)
          ]);

          dispatch(
            new ReceiveExecuteFavourite(instanceKey, favourite, layoutModel, requestId)
          );
        } catch (e) {
          const areaStateAfterFetching = selectChartState(instanceKey)(getState()); 
          if (areaStateAfterFetching.executeRequestId === requestId ) {
            dispatch(new FailExecuteFavourite(instanceKey, !!e.userMessages && e.userMessages.map(m=>m.message)));
          }
        }
      }),
    drillDownToCaseListing: (
      mainFavouriteId: number,
      containerFavouriteId: number,
      drillDownModel: DrillDownModel
    ): ActionDispatching<any, ApplicationState> =>
      errorSafe(async (dispatch, getState) => {
        const result = await api.drillDown(
          mainFavouriteId,
          containerFavouriteId,
          drillDownModel
        );

        routerActions.toCaseListing(result.favourite);
      }),
    onLoadOptionsDrillDownTo: (): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        dispatch(new actions.RequestOptionsDrillDownTo(instanceKey));
        const result = await api.loadOptionsDrillDownTo();
        const optionsDrillDownTo = result.data.rootCodes?.map(codeItemToStoreValue);
        dispatch(new actions.ResponseOptionsDrillDownTo(instanceKey, optionsDrillDownTo));
      }
    ),
    onLoadDrilldownLayoutTypes: (reportId: number): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        if (reportId) {
          dispatch(new actions.RequestDrillDownOptionsLayoutTypes(instanceKey));
          const result = await api.loadLayoutTypes(reportId);
          dispatch(new actions.ResponseDrillDownOptionsLayoutTypes(instanceKey, result.data));
        }
      }),
    setDrillDownReport: (drillDownReport: ComplexValue<number>): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        dispatch(setWorkingDefinitionDrillDownReport(storeValueToCodeItem(drillDownReport)));
      }),

    setDrillDownLayoutType: (layoutType: ComplexValue<number>): ActionDispatching<any, any> =>
        errorSafe(async (dispatch: any, getState: any) => {
          dispatch(setWorkingDefinitionDrillDownLayoutType(storeValueToCodeItem(layoutType)));
        })
  };
};
